import { useCallback } from 'react';
import { GetTeamsLite, TeamLiteItem } from '@/apis/lookup';
import {
  useLiteDropdown,
  UseLiteDropdownOptions,
} from '@/common/helper/useLiteDropdown';
import {
  PaginatedRequest,
  PaginatedResponse,
} from '@/common/helper/usePaginatedDropdown';

export interface UseTeamLiteDropdownParams extends Partial<
  Pick<UseLiteDropdownOptions<TeamLiteItem>, 'initialLimit' | 'initialSearch'>
> {
  unauthorize?: (error: unknown) => void;
}

/**
 * Domain hook for the teams lite dropdown.
 *
 * Calls GET /api/auth/teams/?lite=true with pagination and search.
 * Delegates scroll handling and pagination state to useLiteDropdown.
 */
export const useTeamLiteDropdown = ({
  initialLimit = 20,
  initialSearch = '',
  unauthorize,
}: UseTeamLiteDropdownParams = {}) => {
  const fetchFn = useCallback(
    async ({
      page,
      limit,
      search,
    }: PaginatedRequest): Promise<PaginatedResponse<TeamLiteItem>> => {
      try {
        const response = await GetTeamsLite(page, search || undefined, limit);
        const resData = response?.data?.data;
        const items = Array.isArray(resData?.data) ? resData.data : [];
        const total = resData?.total_count;
        const pageCount = resData?.page_count;
        return {
          data: items,
          total,
          pageCount,
          hasNext: page < pageCount,
        };
      } catch (error) {
        unauthorize?.(error);
        return { data: [], total: 0, pageCount: 0, hasNext: false };
      }
    },
    [unauthorize]
  );

  const lite = useLiteDropdown<TeamLiteItem>({
    fetchFn,
    initialLimit,
    initialSearch,
  });

  return {
    /** Team items for the current loaded pages */
    teamList: lite.data,
    teams: lite.data,
    hasMore: lite.hasMore,
    loading: lite.loading,
    search: lite.search,
    setSearch: lite.setSearch,
    loadMore: lite.loadMore,
    handlePopupScroll: lite.handlePopupScroll,
    fetchAllMatching: lite.fetchAllMatching,
  };
};

export default useTeamLiteDropdown;
